import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import LifecycleOverview from './components/LifecycleOverview';
import EquipmentInventory from './components/EquipmentInventory';
import MaintenanceSchedule from './components/MaintenanceSchedule';
import WorkOrdersPanel from './components/WorkOrdersPanel';
import DepreciationTracker from './components/DepreciationTracker';
import BarcodeScanModal from './components/BarcodeScanModal';
import { equipmentLifecycleService } from '../../lib/equipmentLifecycleService';

const EquipmentLifecycleManagement = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [equipment, setEquipment] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [barcodeScanOpen, setBarcodeScanOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  const hasAccess = ['owner', 'manager', 'sysadmin']?.includes(userProfile?.role);
  const gymId = userProfile?.gym_id;

  useEffect(() => {
    if (hasAccess && gymId) {
      loadData();
    }
  }, [hasAccess, gymId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [equipmentRes, workOrdersRes, analyticsRes] = await Promise.all([
        equipmentLifecycleService?.getEquipment(gymId),
        equipmentLifecycleService?.getWorkOrders(gymId),
        equipmentLifecycleService?.getLifecycleAnalytics(gymId)
      ]);

      if (equipmentRes?.data) setEquipment(equipmentRes?.data);
      if (workOrdersRes?.data) setWorkOrders(workOrdersRes?.data);
      if (analyticsRes?.data) setAnalytics(analyticsRes?.data);
    } catch (error) {
      console.error('Error loading equipment lifecycle data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeScan = (equipmentData) => {
    setSelectedEquipment(equipmentData);
    setBarcodeScanOpen(false);
    loadData();
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader />
        <DashboardNavigation />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-card rounded-xl p-8 border border-border text-center">
            <Icon name="ShieldAlert" size={48} className="mx-auto mb-4 text-error" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You do not have permission to access equipment lifecycle management.</p>
            <p className="text-sm text-muted-foreground mt-2">This feature is only available to gym owners, managers, and system administrators.</p>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Lifecycle Overview', icon: 'Activity' },
    { id: 'inventory', label: 'Equipment Inventory', icon: 'Package' },
    { id: 'maintenance', label: 'Maintenance Schedule', icon: 'Wrench' },
    { id: 'workorders', label: 'Work Orders', icon: 'ClipboardList' },
    { id: 'depreciation', label: 'Depreciation Tracking', icon: 'TrendingDown' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>

      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Equipment Lifecycle Management</h1>
            <p className="text-muted-foreground">Comprehensive tracking from procurement to disposal</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              iconName="Scan"
              iconPosition="left"
              onClick={() => setBarcodeScanOpen(true)}
            >
              Scan Barcode
            </Button>
            <Button
              variant="primary"
              iconName="Plus"
              iconPosition="left"
            >
              Add Equipment
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-card rounded-xl border border-border mb-6">
          <div className="flex items-center gap-2 p-2 overflow-x-auto custom-scrollbar">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg whitespace-nowrap transition-all ${
                  activeTab === tab?.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                <span className="text-sm font-medium">{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading equipment data...</p>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <LifecycleOverview
                equipment={equipment}
                analytics={analytics}
                workOrders={workOrders}
              />
            )}
            {activeTab === 'inventory' && (
              <EquipmentInventory
                equipment={equipment}
                onRefresh={loadData}
              />
            )}
            {activeTab === 'maintenance' && (
              <MaintenanceSchedule
                equipment={equipment}
                onRefresh={loadData}
              />
            )}
            {activeTab === 'workorders' && (
              <WorkOrdersPanel
                workOrders={workOrders}
                equipment={equipment}
                onRefresh={loadData}
              />
            )}
            {activeTab === 'depreciation' && (
              <DepreciationTracker
                equipment={equipment}
                onRefresh={loadData}
              />
            )}
          </>
        )}
      </div>

      {/* Barcode Scan Modal */}
      {barcodeScanOpen && (
        <BarcodeScanModal
          onClose={() => setBarcodeScanOpen(false)}
          onScan={handleBarcodeScan}
          equipment={equipment}
        />
      )}
    </div>
  );
};

export default EquipmentLifecycleManagement;